import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'data';
import { ResourceSection } from 'data/documents';
import { ProjectTemplateTask } from 'data/templates/projects';
import {
  DocumentTemplate,
  DocumentTemplateContentSection,
  DocumentTemplateStore,
} from './types';

export const selectDocumentById =
  (docId: number) =>
  ({ templates }: RootState): DocumentTemplate =>
    templates.document[docId]?.template;

export const selectSections =
  (docId: number, sectionIds: number[]) =>
  ({ templates }: RootState): DocumentTemplateContentSection[] => {
    const ids = sectionIds ?? [];

    return ids.map((sectionId) => {
      return templates.document[docId]?.sections[sectionId];
    });
  };

export const selectResourceSections =
  (docId: number, resourceSectionIds: number[]) =>
  ({
    templates,
  }: RootState): ResourceSection<Partial<ProjectTemplateTask>>[] => {
    return resourceSectionIds.map((resourceSectionId) => {
      return templates.document[docId]?.resourceSections[resourceSectionId];
    });
  };
export const selectResourceSectionById =
  (docId: number, resourceSectionId: number) =>
  ({
    templates,
  }: RootState): ResourceSection<Partial<ProjectTemplateTask>> | undefined => {
    return templates.document[docId]?.resourceSections[resourceSectionId];
  };

export const selectAllDocumentResourceSections =
  (docId: number) =>
  ({
    templates,
  }: RootState): ResourceSection<Partial<ProjectTemplateTask>>[] => {
    return Object.values(templates.document[docId]?.resourceSections || {});
  };

export const selectAllDocumentResourceSectionsAlt = createSelector(
  (state: RootState, docId: number) =>
    state.templates.document[docId]?.resourceSections,
  (resourceSections) => {
    return Object.values(resourceSections || {});
  },
);
// Example usage of selectDocumentById with useSelector
// const docId = 1;
// const documentTemplate = useSelector((state: RootState) => selectDocumentById(docId)(state));

// // Example usage of selectSections with useSelector
// const sectionIds = [1, 2, 3];
// const documentSections = useSelector((state: RootState) => selectSections(docId, sectionIds)(state));

// // Example usage of selectResourceSections with useSelector
// const resourceSectionIds = [1, 2, 3];
// const resourceSections = useSelector((state: RootState) => selectResourceSections(docId, resourceSectionIds)(state));

// // Example usage of selectResourceSectionById with useSelector
// const resourceSectionId = 1;
// const resourceSection = useSelector((state: RootState) => selectResourceSectionById(docId, resourceSectionId)(state));

// // Example usage of selectAllDocumentResourceSections with useSelector
// const allResourceSections = useSelector((state: RootState) => selectAllDocumentResourceSections(state, docId));

export const getDocumentById =
  (id: number) =>
  ({ templates }: RootState): DocumentTemplate | undefined =>
    templates.document[id]?.template;

export const getDocumentTemplateStore =
  (id: number) =>
  ({ templates }: RootState): ValueOf<DocumentTemplateStore> | undefined =>
    templates.document[id];
